import type { NotificationType, ResolvedNotificationTemplate } from "@loot-os/api-contract";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";

import { useStorefrontConfigQuery } from "./storefront";
import { uploadTenantAssetFile } from "./tenantAssets";
import { useTRPC } from "./trpc";

/**
 * What both notification pages read: the system templates with the context a preview renders in
 * (`notifications.templates`), and the storefront config for the environment, its theme and
 * whether this stage takes uploads.
 */
export function useNotificationPageData() {
  const trpc = useTRPC();
  const templates = useQuery(trpc.notifications.templates.queryOptions());
  const { query: storefront } = useStorefrontConfigQuery();
  return { templates, storefront };
}

/** Uploads a banner through the notifications presigned PUT and answers its public URL. */
export function useNotificationImageUpload() {
  const trpc = useTRPC();
  const { mutateAsync: startUpload } = useMutation(
    trpc.notifications.startImageUpload.mutationOptions(),
  );
  const { mutateAsync: finishUpload } = useMutation(
    trpc.notifications.finishImageUpload.mutationOptions(),
  );
  // From the pick until the server has checked the file, the bucket PUT between included.
  const [uploading, setUploading] = useState(false);
  const upload = useCallback(
    async (file: File) => {
      setUploading(true);
      try {
        const { publicUrl } = await uploadTenantAssetFile(
          file,
          (input) => startUpload(input),
          (uploadKey) => finishUpload({ uploadKey }),
        );
        return publicUrl;
      } finally {
        setUploading(false);
      }
    },
    [startUpload, finishUpload],
  );
  return { upload, uploading };
}

/**
 * `updateTemplate` and `resetTemplate`, each writing the template it answers into the
 * `templates` cache before the refetch, so a saved editor never reads as unsaved in between.
 * Callers add their own toast per call.
 */
export function useNotificationTemplateWrites() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const write = (
    type: NotificationType,
    template: ResolvedNotificationTemplate,
    customized: boolean,
  ) => {
    queryClient.setQueryData(trpc.notifications.templates.queryKey(), (old) =>
      old
        ? {
            ...old,
            templates: old.templates.map((row) =>
              row.type === type ? { ...row, template, customized } : row,
            ),
          }
        : old,
    );
    void queryClient.invalidateQueries({ queryKey: trpc.notifications.templates.pathKey() });
  };
  const update = useMutation(
    trpc.notifications.updateTemplate.mutationOptions({
      onSuccess: (template, input) => write(input.type, template, true),
    }),
  );
  const reset = useMutation(
    trpc.notifications.resetTemplate.mutationOptions({
      onSuccess: (template, input) => write(input.type, template, false),
    }),
  );
  return { update, reset };
}
